import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react'

import { getVisibleRoutineCategories } from './routinePresentation'

import type { RoutineCategory } from './types'

export const useRoutinePlayer = (routines: RoutineCategory[]) => {
  const audioRef = useRef<HTMLAudioElement>(null)
  const autoplayRequestRef = useRef(false)
  const [browseCategoryIndex, setBrowseCategoryIndex] = useState(0)
  const [playbackCategoryIndex, setPlaybackCategoryIndex] = useState(0)
  const [playbackTrackIndex, setPlaybackTrackIndex] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  const availableRoutines = useMemo(
    () => getVisibleRoutineCategories(routines),
    [routines],
  )
  const browsedRoutine = availableRoutines[browseCategoryIndex]
  const browsedTracks = browsedRoutine?.content ?? []
  const playbackRoutine = availableRoutines[playbackCategoryIndex]
  const playbackTracks = playbackRoutine?.content ?? []
  const track = playbackTracks[playbackTrackIndex]
  const progressPercentage =
    duration > 0
      ? Math.min(100, Math.max(0, (currentTime / duration) * 100))
      : 0

  useEffect(() => {
    if (availableRoutines.length === 0) {
      setBrowseCategoryIndex(0)
      setPlaybackCategoryIndex(0)
      setPlaybackTrackIndex(0)
      setPlaying(false)
      setCurrentTime(0)
      setDuration(0)
      return
    }

    if (browseCategoryIndex >= availableRoutines.length) {
      setBrowseCategoryIndex(availableRoutines.length - 1)
    }

    if (playbackCategoryIndex >= availableRoutines.length) {
      setPlaybackCategoryIndex(availableRoutines.length - 1)
      setPlaybackTrackIndex(0)
      setPlaying(false)
      setCurrentTime(0)
      setDuration(0)
      return
    }

    if (playbackTrackIndex >= playbackTracks.length) {
      setPlaybackTrackIndex(Math.max(0, playbackTracks.length - 1))
      setPlaying(false)
      setCurrentTime(0)
      setDuration(0)
    }
  }, [
    availableRoutines,
    browseCategoryIndex,
    playbackCategoryIndex,
    playbackTrackIndex,
    playbackTracks.length,
  ])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !track) return

    audio.load()
    setCurrentTime(0)
    setDuration(0)

    if (autoplayRequestRef.current) {
      autoplayRequestRef.current = false
      void audio.play().then(
        () => setPlaying(true),
        () => setPlaying(false),
      )
    }
  }, [track])

  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio || !track) return

    if (playing) {
      audio.pause()
    } else {
      void audio.play().catch(() => setPlaying(false))
    }
    setPlaying((value) => !value)
  }

  const selectTrack = (index: number) => {
    const isCurrentTrack =
      browseCategoryIndex === playbackCategoryIndex &&
      index === playbackTrackIndex

    if (isCurrentTrack) {
      togglePlay()
      return
    }

    autoplayRequestRef.current = true
    setPlaybackCategoryIndex(browseCategoryIndex)
    setPlaybackTrackIndex(index)
  }

  const step = (direction: number) => {
    if (!playbackTracks.length) return

    const nextIndex =
      (playbackTrackIndex + direction + playbackTracks.length) %
      playbackTracks.length

    if (nextIndex === playbackTrackIndex) {
      togglePlay()
      return
    }

    autoplayRequestRef.current = true
    setPlaybackTrackIndex(nextIndex)
  }

  const seek = (value: number) => {
    if (audioRef.current) audioRef.current.currentTime = value
    setCurrentTime(value)
  }

  const handleLoadedMetadata = (event: SyntheticEvent<HTMLAudioElement>) => {
    setDuration(event.currentTarget.duration)
  }

  const handleTimeUpdate = (event: SyntheticEvent<HTMLAudioElement>) => {
    setCurrentTime(event.currentTarget.currentTime)
  }

  const handleEnded = () => {
    setPlaying(false)
    setCurrentTime(0)
  }

  const isTrackPlaying = (categoryIndex: number, trackIndex: number) =>
    playing &&
    categoryIndex === playbackCategoryIndex &&
    trackIndex === playbackTrackIndex

  return {
    audioRef,
    availableRoutines,
    browsedRoutine,
    browsedTracks,
    browseCategoryIndex,
    currentTime,
    duration,
    playbackRoutine,
    playing,
    progressPercentage,
    track,
    handleEnded,
    handleLoadedMetadata,
    handleTimeUpdate,
    isTrackPlaying,
    seek,
    selectTrack,
    setBrowseCategoryIndex,
    step,
    togglePlay,
  }
}
